import {
  BrowserRouter,
  Routes,
  Route,
  Outlet,
  Navigate,
} from "react-router-dom";

import { ThemeProvider } from "@mui/material/styles";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import ConstantProvider from "components/context/ConstantProvider";
import { OnBeforeUnloadProvider } from "components/context/OnBeforeUnloadProvider";
import SnackbarProvider from "components/context/SnackbarProvider";
import theme from "components/context/ThemeProvider";
import { TypesenseProvider } from "components/context/TypesenseInstantSearch";
import UserProvider from "components/context/UserProvider";
import AuthenticationProvider from "components/context/AuthenticationProvider";
import { SearchContextProvider } from "components/ui/atoms/SearchDrawerButton";
import useVersionCheck from "components/hooks/useVersionCheck";
import NiceModalProvider from "components/context/NiceModalProvider";

import Home from "./home";
import Account from "./models/accounts/account-page";
import AutomatedTasks from "./models/automated_tasks/automatedTasks-page";

import CommitteeMinute from "./models/committee_minute/committee_minute-page";
import CommitteeMinutePrint from "./models/committee_minute/committee_minute_print-page";
import CommitteeMinutePreview from "./models/committee_minute/committee_minute_current-page";

import Companies from "./models/companies/companies-page";
import Company from "./models/companies/company-page";
import Deals from "./models/deals/deals-page";
import Sourcings from "./models/sourcings/sourcings-page";
import Notes from "./models/notes/notes-page";
import Note from "./models/notes/note-page";
import People from "./models/people/people-page";
import Person from "./models/people/person-page";
import BackofficeRoutes from "./models/backoffice/BackofficeRoutes";
import MyOfficeRoutes from "models/myoffice/MyOfficeRoutes";
import { useMemo } from "react";

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false, // default: true
    },
  },
});

const GlobalLayout = () => {
  return (
    <ThemeProvider theme={theme}>
      <SnackbarProvider>
        <AuthenticationProvider>
          <QueryClientProvider client={queryClient}>
            {false && <ReactQueryDevtools />}
            <UserProvider>
              <OnBeforeUnloadProvider>
                <Outlet />
              </OnBeforeUnloadProvider>
            </UserProvider>
          </QueryClientProvider>
        </AuthenticationProvider>
      </SnackbarProvider>
    </ThemeProvider>
  );
};

const SocialLayout = () => {
  return (
    <ConstantProvider>
      <TypesenseProvider>
        <SearchContextProvider>
          <NiceModalProvider>
            <Outlet />
          </NiceModalProvider>
        </SearchContextProvider>
      </TypesenseProvider>
    </ConstantProvider>
  );
};

const Page = () => {
  useVersionCheck();

  return (
    <BrowserRouter basename="/">
      <Routes>
        <Route element={<GlobalLayout />}>
          <Route element={<SocialLayout />}>
            <Route index element={<Home />} />
            <Route path="account/" element={<Account />} />
            <Route path="automated_tasks/" element={<AutomatedTasks />} />
            <Route
              path="committee_minute/:committee_minute_id"
              element={<CommitteeMinute />}
            />
            <Route
              path="committee_minute/:committee_minute_id/print"
              element={<CommitteeMinutePrint />}
            />
            <Route
              path="committee_minute/preview/:deal_flow_id"
              element={<CommitteeMinutePreview />}
            />
            <Route path="company" element={<Companies />} />
            <Route path="company/:company_id" element={<Company />} />
            <Route path="deal" element={<Deals />} />
            <Route path="sourcing" element={<Sourcings />} />
            <Route path="note" element={<Notes />} />
            <Route path="note/:note_id" element={<Note />} />
            <Route path="people" element={<People />} />
            <Route path="people/:people_id" element={<Person />} />
            {/* Catch-all route for any other paths */}
          </Route>
          {BackofficeRoutes}
          {MyOfficeRoutes}
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </BrowserRouter>
  );
};

export default Page;
